import { EditorState } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { createFileLogger } from '../../../utils/logger';

const log = createFileLogger('HOTKEY_REGISTRY');

export interface HotkeyRegistration {
  id: string;
  keys: string; // e.g., "Mod-Shift-c"
  description: string;
  handler: (view: EditorView) => boolean;
  condition?: (view: EditorView) => boolean; // Optional condition check
}

/**
 * Registry for plugins to register custom hotkeys
 */
class HotkeyRegistry {
  private hotkeys: Map<string, HotkeyRegistration> = new Map();
  
  /**
   * Register a hotkey from a plugin
   */
  register(registration: HotkeyRegistration): void {
    if (this.hotkeys.has(registration.id)) {
      log.warn(`[HotkeyRegistry] Hotkey '${registration.id}' already registered, overwriting`);
    }
    
    this.hotkeys.set(registration.id, registration);
    log.info(`[HotkeyRegistry] Registered hotkey: ${registration.keys} for ${registration.id}`);
  }
  
  /**
   * Unregister a hotkey
   */
  unregister(id: string): void {
    this.hotkeys.delete(id);
    log.info(`[HotkeyRegistry] Unregistered hotkey: ${id}`);
  }
  
  /**
   * Get all registered hotkeys
   */
  getAllHotkeys(): HotkeyRegistration[] {
    return Array.from(this.hotkeys.values());
  }
  
  /**
   * Get hotkey registration by ID
   */
  getHotkey(id: string): HotkeyRegistration | undefined {
    return this.hotkeys.get(id);
  }
  
  /**
   * Build keymap object for ProseMirror
   * Returns commands with proper signature: (state, dispatch?, view?) => boolean
   */
  buildKeymap(): Record<string, (state: EditorState, dispatch?: any, view?: EditorView) => boolean> {
    const keymap: Record<string, (state: EditorState, dispatch?: any, view?: EditorView) => boolean> = {};
    
    for (const registration of this.hotkeys.values()) {
      keymap[registration.keys] = (state: EditorState, dispatch?: any, view?: EditorView) => {
        if (!view) return false;
        
        // Check condition if exists
        if (registration.condition && !registration.condition(view)) {
          return false;
        }
        
        return registration.handler(view);
      };
    }
    
    return keymap;
  }
  
  /**
   * Reset registry
   */
  reset(): void {
    this.hotkeys.clear();
    log.info('[HotkeyRegistry] Registry reset');
  }
}

export const hotkeyRegistry = new HotkeyRegistry();

